import React, { useState, useEffect } from 'react';
import { Star, Quote, Calendar, MapPin, Loader2, MessageSquarePlus, Send, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Temoignages = () => {
  const { toast } = useToast();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, 5stars, 4stars

  // Formulaire public "Laisser un avis"
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    name: '',
    email: '',
    location: '',
    vehicle: '',
    rating: 5,
    text: '',
    savings: '',
  });

  const handleReviewChange = (e) => {
    const { name, value } = e.target;
    setReviewForm(prev => ({
      ...prev,
      [name]: name === 'rating' ? parseInt(value) : value,
    }));
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/reviews/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reviewForm),
      });
      if (res.ok || res.status === 202) {
        setSubmitted(true);
        toast({
          title: '✅ Merci pour votre avis !',
          description: 'Votre témoignage sera publié après vérification.',
        });
      } else {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Erreur d\'envoi');
      }
    } catch (err) {
      toast({
        title: '❌ Erreur',
        description: 'Votre avis n\'a pas pu être envoyé. Veuillez réessayer.',
        variant: 'destructive',
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reviews`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des avis:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredReviews = reviews.filter(review => {
    if (filter === '5stars') return review.rating === 5;
    if (filter === '4stars') return review.rating === 4;
    return true;
  }).sort((a, b) => new Date(b.date) - new Date(a.date)); // Tri par date décroissante (plus récent en premier)

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  const ratingDistribution = [5, 4, 3, 2, 1].map(rating => ({
    stars: rating,
    count: reviews.filter(r => r.rating === rating).length,
    percentage: reviews.length > 0 
      ? (reviews.filter(r => r.rating === rating).length / reviews.length) * 100
      : 0
  }));

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Témoignages Clients
            </h1>
            <p className="text-lg md:text-xl opacity-90">
              Découvrez ce que nos clients pensent d'Ecopra Suisse
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-5xl font-bold text-[#7CB342] mb-2">{reviews.length}</div>
                <div className="text-gray-600">Avis clients</div>
              </div>
              <div>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-5xl font-bold text-[#00BCD4]">{averageRating}</span>
                  <Star className="w-8 h-8 fill-yellow-400 text-yellow-400" />
                </div>
                <div className="text-gray-600">Note moyenne</div>
              </div>
              <div>
                <div className="text-5xl font-bold bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent mb-2">
                  {reviews.filter(r => r.rating === 5).length}
                </div>
                <div className="text-gray-600">Avis 5 étoiles</div>
              </div>
            </div>

            {/* Rating Distribution */}
            <div className="mt-12 max-w-md mx-auto">
              <h3 className="text-xl font-semibold text-center mb-6">Répartition des notes</h3>
              {ratingDistribution.map(({ stars, count, percentage }) => (
                <div key={stars} className="flex items-center gap-3 mb-3">
                  <div className="flex items-center gap-1 w-20">
                    <span className="font-semibold">{stars}</span>
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  </div>
                  <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] h-full rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-600 w-12">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto flex justify-center gap-4 flex-wrap">
            <button
              onClick={() => setFilter('all')}
              className={`px-6 py-2 rounded-full font-semibold transition-all ${
                filter === 'all'
                  ? 'bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Tous les avis ({reviews.length})
            </button>
            <button
              onClick={() => setFilter('5stars')}
              className={`px-6 py-2 rounded-full font-semibold transition-all ${
                filter === '5stars'
                  ? 'bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              5 étoiles ({reviews.filter(r => r.rating === 5).length})
            </button>
            <button
              onClick={() => setFilter('4stars')}
              className={`px-6 py-2 rounded-full font-semibold transition-all ${
                filter === '4stars'
                  ? 'bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              4 étoiles ({reviews.filter(r => r.rating === 4).length})
            </button>
          </div>
        </div>
      </section>

      {/* Reviews List */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-12 h-12 animate-spin text-[#7CB342]" />
              </div>
            ) : filteredReviews.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-xl text-gray-500">Aucun avis pour ce filtre</p>
              </div>
            ) : (
              <div className="grid gap-6">
                {filteredReviews.map((review) => (
                  <Card key={review.id} className="hover:shadow-xl transition-shadow duration-300 border-l-4 border-[#00BCD4]">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4 mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-gray-800">{review.name}</h3>
                            {review.rating === 5 && (
                              <span className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white text-xs px-3 py-1 rounded-full font-semibold">
                                Client satisfait
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 mb-2">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-5 h-5 ${
                                  i < review.rating
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'fill-gray-200 text-gray-200'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <div className="text-right text-sm text-gray-500">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(review.date).toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </div>
                          {review.source && (
                            <div className="flex items-center gap-1 mt-1">
                              <MapPin className="w-4 h-4" />
                              {review.source}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="relative">
                        <Quote className="absolute -top-2 -left-2 w-8 h-8 text-[#00BCD4] opacity-20" />
                        <p className="text-gray-700 leading-relaxed pl-6 italic">
                          "{review.text}"
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Formulaire public "Laisser mon avis" */}
      <section className="py-16 bg-gradient-to-br from-[#7CB342]/5 to-[#00BCD4]/5 border-t border-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#7CB342] to-[#00BCD4] mb-4 shadow-lg">
                <MessageSquarePlus className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-3 text-gray-800">
                Vous êtes équipé d'un Kit Ecopra ?
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Partagez votre expérience pour aider d'autres conducteurs en Suisse à découvrir les bénéfices du Kit Buveur d'Air.
              </p>
            </div>

            {!showForm && !submitted && (
              <div className="text-center">
                <button
                  onClick={() => setShowForm(true)}
                  data-testid="open-review-form-btn"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white font-bold px-8 py-4 rounded-full hover:shadow-2xl hover:scale-105 transition-all"
                >
                  <MessageSquarePlus className="w-5 h-5" />
                  Laisser mon avis
                </button>
              </div>
            )}

            {submitted && (
              <div className="bg-white rounded-2xl shadow-xl p-8 text-center border-2 border-green-200" data-testid="review-thanks-card">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Merci pour votre témoignage !</h3>
                <p className="text-gray-600">
                  Nous l'avons bien reçu. Notre équipe le publiera sur cette page après une rapide vérification.
                  Une copie de votre avis vous a été envoyée par email.
                </p>
              </div>
            )}

            {showForm && !submitted && (
              <form
                onSubmit={handleReviewSubmit}
                data-testid="public-review-form"
                className="bg-white rounded-2xl shadow-xl p-6 md:p-8 space-y-5"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Votre nom *</label>
                    <input
                      type="text" name="name" required minLength={2}
                      value={reviewForm.name} onChange={handleReviewChange}
                      data-testid="review-name-input"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00BCD4] focus:border-transparent"
                      placeholder="Jean Dupont"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Email *</label>
                    <input
                      type="email" name="email" required
                      value={reviewForm.email} onChange={handleReviewChange}
                      data-testid="review-email-input"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00BCD4] focus:border-transparent"
                      placeholder="vous@email.ch"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Localisation</label>
                    <input
                      type="text" name="location"
                      value={reviewForm.location} onChange={handleReviewChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00BCD4] focus:border-transparent"
                      placeholder="Sion, Genève, Lausanne…"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Véhicule</label>
                    <input
                      type="text" name="vehicle"
                      value={reviewForm.vehicle} onChange={handleReviewChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00BCD4] focus:border-transparent"
                      placeholder="BMW X5, Peugeot 308, tracteur…"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Votre note *</label>
                  <div className="flex items-center gap-2" data-testid="review-rating-stars">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        type="button"
                        key={n}
                        onClick={() => setReviewForm(prev => ({ ...prev, rating: n }))}
                        className="transition-transform hover:scale-110"
                        aria-label={`Note ${n} étoile${n > 1 ? 's' : ''}`}
                      >
                        <Star
                          className={`w-9 h-9 ${
                            n <= reviewForm.rating
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'fill-gray-200 text-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                    <span className="ml-3 text-gray-600 font-semibold">{reviewForm.rating}/5</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Votre témoignage *</label>
                  <textarea
                    name="text" required minLength={10} rows={5}
                    value={reviewForm.text} onChange={handleReviewChange}
                    data-testid="review-text-input"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00BCD4] focus:border-transparent resize-none"
                    placeholder="Racontez votre expérience avec le Kit Ecopra : économies réalisées, ressenti à la conduite, qualité du produit…"
                  />
                  <p className="text-xs text-gray-400 mt-1">Minimum 10 caractères.</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Économies constatées (optionnel)</label>
                  <input
                    type="text" name="savings"
                    value={reviewForm.savings} onChange={handleReviewChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00BCD4] focus:border-transparent"
                    placeholder="Ex : 20% d'économie, de 8L à 6,5L/100km…"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    type="submit" disabled={submitLoading}
                    data-testid="submit-review-btn"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white font-bold px-8 py-3 rounded-lg hover:shadow-xl transition-all disabled:opacity-60"
                  >
                    {submitLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    Envoyer mon avis
                  </button>
                  <button
                    type="button" onClick={() => setShowForm(false)}
                    className="px-6 py-3 border border-gray-300 rounded-lg font-semibold text-gray-600 hover:bg-gray-50 transition"
                  >
                    Annuler
                  </button>
                </div>

                <p className="text-xs text-gray-400 text-center">
                  Votre email reste confidentiel et ne sera pas affiché publiquement.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Rejoignez nos clients satisfaits !
          </h2>
          <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
            Découvrez les économies et performances qu'Ecopra peut apporter à votre véhicule
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <a
              href="/boutique"
              className="bg-white text-[#7CB342] px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors shadow-lg"
            >
              Voir la boutique
            </a>
            <a
              href="/contact"
              className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg font-bold hover:bg-white/10 transition-colors"
            >
              Nous contacter
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Temoignages;
